<template>
  <div class="map-overlays">
    <div
      v-show="speciesTip.show"
      class="species-hex-tip"
      :style="{ left: speciesTip.x + 'px', top: speciesTip.y + 'px' }"
      v-html="speciesTip.html"
    ></div>
    <div
      v-show="soilTip.show"
      class="species-hex-tip"
      :style="{ left: soilTip.x + 'px', top: soilTip.y + 'px' }"
      v-html="soilTip.html"
    ></div>
    <div
      v-show="customTip.show"
      class="species-hex-tip"
      :style="{ left: customTip.x + 'px', top: customTip.y + 'px' }"
      v-html="customTip.html"
    ></div>
    <div class="map-legends">
      <div v-if="soilVisible" class="map-legend" @mouseenter="hideDataTips">
        <div class="map-legend-header">
          <span class="map-legend-title">{{ $t('operability') }}</span>
          <Icon icon="mdi-close" iconColor="white" margin="" class="map-legend-clear" :tooltipText="$t('clear')" @icon-clicked="clearSoilOperability"/>
        </div>
        <div v-for="item in soilLegend" :key="item.rating" class="map-legend-row">
          <span class="map-legend-swatch" :style="{ background: item.color }"></span>
          <span>{{ item.rating }}</span>
        </div>
        <div class="map-legend-alpha">
          <span class="map-legend-alpha-label">{{ $t('opacity') }}</span>
          <input
            type="range"
            class="map-legend-slider"
            :min="minOpacity"
            max="1"
            step="0.05"
            :value="soilOpAlpha"
            @input="soilOpAlpha = Number($event.target.value)"
          />
          <span class="map-legend-alpha-value">{{ Math.round(soilOpAlpha * 100) }}%</span>
        </div>
      </div>
      <div v-if="nearbyBoundariesVisible" class="map-legend" @mouseenter="hideDataTips">
        <div class="map-legend-header">
          <span class="map-legend-title">{{ $t('nearbyBoundaries') }}</span>
          <Icon icon="mdi-close" iconColor="white" margin="" class="map-legend-clear" :tooltipText="$t('clear')" @icon-clicked="hideNearbyTractBoundaries"/>
        </div>
        <div v-for="row in nearbyBoundaryRows" :key="row.status.value" class="map-legend-row">
          <span class="map-legend-swatch" :style="{ background: row.swatch.color, border: row.swatch.border }"></span>
          <span>{{ $t('nearbyBoundariesWithinMiles', { status: row.status.name, count: row.count, miles: NEARBY_TRACT_RADIUS_MILES }) }}</span>
        </div>
      </div>
      <div v-if="customPanelOpen" class="map-legend custom-layers-panel" @mouseenter="hideDataTips">
        <div class="map-legend-header">
          <span class="map-legend-title">{{ $t('layerManagement') }}</span>
          <Icon icon="mdi-close" iconColor="white" margin="" class="map-legend-clear" :tooltipText="$t('close')" @icon-clicked="customPanelOpen = false"/>
        </div>
        <v-progress-circular v-if="customLayersLoading && customLayers.length === 0" indeterminate :size="16" :width="2" color="#D15F27" />
        <div v-else-if="customLayers.length === 0" class="custom-layer-note">{{ $t('noCustomLayers') }}</div>
        <draggable :value="panelLayers" animation="200" handle=".custom-layer-grip" @input="reorderPanelLayers">
        <div v-for="row in panelLayers" :key="row.key" class="custom-layer">
          <div v-if="row.kind === 'species'" class="map-legend-row">
            <v-icon small color="white" class="custom-layer-grip">mdi-drag-vertical</v-icon>
            <label class="map-legend-row custom-layer-toggle">
              <input type="checkbox" :checked="showSpeciesLayer" @click.prevent="$emit('toggle-species')" />
              <span class="custom-layer-name">{{ $t('speciesLayer') }}</span>
            </label>
            <v-progress-circular v-if="speciesLayerEnabling" indeterminate :size="12" :width="2" color="#D15F27"/>
          </div>
          <template v-else>
            <div class="map-legend-row">
              <v-icon small color="white" class="custom-layer-grip">mdi-drag-vertical</v-icon>
              <label class="map-legend-row custom-layer-toggle">
                <input
                  type="checkbox"
                  :checked="row.layer.visible"
                  @change="setCustomLayerVisible(row.layer.name, $event.target.checked)"
                />
                <span class="custom-layer-name">{{ row.layer.label }}</span>
              </label>
              <v-progress-circular v-if="row.layer.loading" indeterminate :size="12" :width="2" color="#D15F27"/>
              <button
                type="button"
                class="custom-layer-color"
                :style="{ background: row.layer.color }"
                :title="$t('color')"
                @click="toggleCustomColorPicker(row.layer.name)"
              ></button>
            </div>
            <v-color-picker
              v-if="customColorEditing === row.layer.name"
              :value="customPickerValue(row.layer)"
              mode="hexa"
              hide-mode-switch
              flat
              dark
              :width="232"
              :canvas-height="120"
              class="custom-layer-picker"
              @input="setCustomLayerStyle(row.layer.name, $event)"
            />
            <div class="custom-layer-note">{{ $t('lastUpdated') }}: {{ utcToLocalDate(row.layer.lastModified, 'L - LT') }}</div>
          </template>
        </div>
        </draggable>
      </div>
    </div>
    <div v-if="soilValidating" class="harvest-validating">
      <v-progress-circular indeterminate :size="22" :width="3" color="#D15F27" />
      <span>{{ $t('loadingOperability') }}</span>
    </div>
    <div v-if="nearbyTractsLoading || nearbyBoundariesLoading" class="harvest-validating">
      <v-progress-circular :indeterminate="nearbyTractsLoading" :value="nearbyBoundariesPercent" :size="22" :width="3" color="#D15F27" />
      <span>{{ nearbyTractsLoading ? $t('loadingNearbyTracts') : $t('loadingNearbyBoundaries', nearbyBoundariesProgress) }}</span>
    </div>
    <div v-if="overlayZoomPegged" class="map-overlay-warning">
      {{ $t('overlayZoomOutLocked') }}
    </div>
    <div v-if="nearbyTractsHidden" class="map-overlay-warning">
      {{ $t('zoomInToViewTracts') }}
    </div>
  </div>
</template>

<script>
import * as atlas from 'azure-maps-control'
import { mapGetters, mapActions } from 'vuex'
import draggable from 'vuedraggable'
import { speciesLayerEnabled } from '@/utils/dueDiligence/mode.js'
import { createPolygonLayersForDataSource, PolygonStyle, styleFromPolygonType } from '@/utils/map/MapHelpers'
import { tilesForBounds, fetchTile, MAX_TILES, SHARD_ZOOM } from '@/utils/map/AngelMapClient.js'
import { adaptFeature, resolveFill, normalizeGroups } from '@/utils/map/shardAdapter.js'
import { MAP_OVERLAYS } from '@/utils/map/overlayLayers.js'
import { fetchHarvestOperability, SOIL_OP_FILL_COLOR, SOIL_OP_FILL_OPACITY, SOIL_OP_LEGEND } from '@/utils/map/soilOperability.js'
import { listCustomLayers, fetchCustomLayerFeatures, layerLabel, CUSTOM_LAYER_PALETTE, CUSTOM_LAYER_DEFAULT_OPACITY } from '@/utils/map/CustomLayerClient.js'
import { utcToLocalDate } from '@/utils/DateFormatter.js'
import { angelGeojsonClient, AngelGeojson, AngelGeojsonLayer } from '@/utils/map/Geojson.js'
import { milesBetween } from '@/utils/Distance.js'
import { harvestedProgress, tractMarkerSvg, tractPopup, viewportWidthMiles, MAX_VIEWPORT_MILES_FOR_TRACT_MARKERS, TRACT_STATUS_COLORS } from '@/utils/map/TractMarkers.js'
import { TractStatus } from '@/utils/Enumerations.js'
import { TractFilter } from '@/model/Tract.js'
import { LruCache } from '@/utils/LruCache.js'
import { LocalStorageKeys } from '@/utils/LocalStorageActor'
import { highestPriorityGroup, matchedGroupsByPrecedence } from '@/utils/dueDiligence/groupPrecedence.js'
import { getParcelDetailsByRobustId, getParcelVectorTilesUrl } from '@/utils/map/Parcel.js'
import * as turf from '@turf/turf'

const OVERLAY_MIN_OPACITY = 0.05

const readStoredOpacity = () => {
  const maxOpacity = 1
  const stored = Number(localStorage.getItem(LocalStorageKeys.OPERABILITY_LAYER_OPACITY))
  return stored >= OVERLAY_MIN_OPACITY && stored <= maxOpacity ? stored : SOIL_OP_FILL_OPACITY
}

const CUSTOM_TIP_MAX_ROWS = 20

const readCustomLayerStyles = () => JSON.parse(localStorage.getItem(LocalStorageKeys.CUSTOM_LAYER_STYLES) ?? '{}')

const writeCustomLayerStyles = (layers) => {
  const styles = { ...readCustomLayerStyles(), ...Object.fromEntries(layers.map((l, order) => [l.name, { color: l.color, opacity: l.opacity, visible: l.visible, order }])) }
  localStorage.setItem(LocalStorageKeys.CUSTOM_LAYER_STYLES, JSON.stringify(styles))
}

const readStoredSpeciesOrder = () => {
  const stored = localStorage.getItem(LocalStorageKeys.SPECIES_LAYER_ORDER)
  return stored === null ? Infinity : Number(stored)
}

const OVERLAY_MIN_ZOOM = SHARD_ZOOM // shards don't exist below their native zoom
const HARVEST_MIN_ZOOM = 14
const NEARBY_TRACT_RADIUS_MILES = 5
const NEARBY_TRACT_STATUSES = [TractStatus.Initiated, TractStatus.Cruised, TractStatus.Executed, TractStatus.Active, TractStatus.Closed]
const nearbyBoundaryStyle = status => ({ ...PolygonStyle.NearbyTractBoundaries, fill: TRACT_STATUS_COLORS[status.value]?.fill ?? PolygonStyle.NearbyTractBoundaries.fill })
const MIN_ZOOM_FOR_PARCEL_TILES = 14
const CUT = AngelGeojsonLayer.Cut
const SPECIES_TILE_CAPACITY = 200
const SPECIES_LIST_MAX = 15
const ANGEL_SECTION_ORDER = ['species']
const ANGEL_RENDERERS = { list: 'renderList' }
export default {
  name: 'MapOverlays',

  components: {
    draggable,
    Icon: () => import('@/components/helper/Icon.vue')
  },

  props: {
    map: { type: Object, default: null },
    autoShowSpecies: { type: Boolean, default: false },
    suppressTips: { type: Boolean, default: false },
    layerCeiling: { type: String, default: 'labels' }
  },

  data: () => ({
    showSpeciesLayer: false,
    speciesSource: null,
    speciesLayers: null,
    speciesFillLayer: null,
    speciesTip: { show: false, x: 0, y: 0, html: '' },
    speciesEventsBound: false,
    speciesOrder: readStoredSpeciesOrder(),
    speciesLayerEnabling: false,
    topoLayer: null,
    topoVisible: false,
    soilVisible: false,
    soilOpSource: null,
    soilOpLayers: null,
    soilOpFillLayer: null,
    soilOpEventsBound: false,
    soilOpReqSeq: 0,
    soilOpAbort: null,
    soilValidating: false,
    soilOpAlpha: readStoredOpacity(),
    minOpacity: OVERLAY_MIN_OPACITY,
    soilLegend: SOIL_OP_LEGEND,
    soilTip: { show: false, x: 0, y: 0, html: '' },
    soilOverFeature: false,
    customLayers: [],
    customLayersLoading: false,
    customPanelOpen: false,
    customColorEditing: null,
    customTip: { show: false, x: 0, y: 0, html: '' },
    overlayEventsBound: false,
    NEARBY_TRACT_RADIUS_MILES,
    nearbyTracts: null,
    nearbyTractsPromise: null,
    nearbyTractsVisible: false,
    nearbyTractsLoading: false,
    nearbyTractsHidden: false,
    nearbyTractsReqSeq: 0,
    openNearbyMarker: { marker: null, justAdded: false },
    nearbyBoundariesVisible: false,
    nearbyBoundariesLoading: false,
    nearbyBoundaryRows: [],
    nearbyBoundariesProgress: { done: 0, total: 0 },
    nearbyBoundarySources: null,
    nearbyBoundariesLayers: null,
    nearbyBoundariesReqSeq: 0,
    nearbyBoundaryCache: new Map(),
    nearbyParcelsVisible: false,
    parcelTilesDataSource: null,
    parcelTileLayers: null,
    parcelTileFillLayer: null,
    focusedParcelDataSource: null,
    focusedParcelLayers: null,
    parcelPopup: null,
    parcelPopupReqSeq: 0,
    currentZoom: null,
    restrictedPrevMinZoom: null,
    mapBound: false
  }),

  computed: {
    ...mapGetters('mitigation-group', ['allMitigationGroups']),
    ...mapGetters('tract-hud', ['allTractHudSections']),

    canShowSpeciesLayer () {
      return speciesLayerEnabled()
    },

    speciesLayerConfig () {
      if (this.allMitigationGroups.length === 0) return null
      const groupsById = new Map(this.allMitigationGroups.map(g => [g.mitigationGroupId, g]))
      return {
        owningGroup: entries => highestPriorityGroup(entries, groupsById),
        groupsByPrecedence: entries => matchedGroupsByPrecedence(entries, groupsById),
        fillFor: entries => highestPriorityGroup(entries, groupsById)?.color ?? null,
        nameFor: entry => groupsById.get(entry?.id)?.name ?? null
      }
    },

    overlayZoomPegged () {
      return this.showSpeciesLayer && this.currentZoom != null && this.currentZoom <= OVERLAY_MIN_ZOOM + 0.01
    },

    panelLayers () {
      const rows = this.customLayers.map(layer => ({ kind: 'custom', key: layer.name, layer }))
      if (this.showSpeciesLayer) rows.splice(Math.min(this.speciesOrder, rows.length), 0, { kind: 'species', key: 'species' })
      return rows
    },

    nearbyBoundariesPercent () {
      const { done, total } = this.nearbyBoundariesProgress
      return total ? done / total * 100 : 0
    }
  },

  watch: {
    canShowSpeciesLayer: 'emitState',
    showSpeciesLayer: 'emitState',
    allMitigationGroups: 'repaintSpeciesLayer',
    topoVisible: 'emitState',
    soilVisible: 'emitState',
    customLayers: { handler: 'emitState', deep: true },
    customPanelOpen: 'emitState',
    nearbyTractsVisible: 'emitState',
    nearbyBoundariesVisible: 'emitState',
    nearbyParcelsVisible: 'emitState',
    soilOpAlpha (value) {
      if (this.soilOpFillLayer) this.soilOpFillLayer.setOptions({ fillOpacity: value })
      localStorage.setItem(LocalStorageKeys.OPERABILITY_LAYER_OPACITY, value)
    },
    suppressTips (value) {
      if (value) {
        this.clearSpeciesPopup()
        this.clearCustomTip()
      }
    },
    map: { immediate: true, handler (m) { if (m) this.bindMap() } }
  },

  created () {
    this.customLayerHandles = {}
    this.nearbyGatedTracts = []
    this.nearbyTractMarkers = new Map()
  },

  mounted () {
    this.emitState()
  },

  methods: {
    ...mapActions('snackbar', ['setSnack', 'setSnackError']),
    ...mapActions('mitigation-group', ['fetchMitigationGroups']),
    ...mapActions('tract', ['fetchTractsOnce']),
    ...mapActions('tract-hud', ['fetchTractHudSections']),
    utcToLocalDate,

    emitState () {
      this.$emit('state', {
        species: { canShow: this.canShowSpeciesLayer, active: this.showSpeciesLayer },
        topo: { active: this.topoVisible },
        soil: { active: this.soilVisible },
        custom: { active: this.customLayers.some(l => l.visible), open: this.customPanelOpen },
        nearbyTracts: { active: this.nearbyTractsVisible },
        nearbyBoundaries: { active: this.nearbyBoundariesVisible },
        nearbyParcels: { active: this.nearbyParcelsVisible }
      })
    },

    bindMap () {
      if (this.mapBound) return
      this.mapBound = true
      this.map.events.add('zoomend', this.updateZoom)
      this.map.events.add('moveend', this.syncNearbyTractMarkers)
      this.map.events.add('click', this.closeNearbyPopup)
      if (this.autoShowSpecies && this.canShowSpeciesLayer) this.map.events.add('ready', () => this.autoEnableSpeciesLayer(true))
    },

    // 'ready' can fire mid camera-fly, so a too-far-out map gets one retry once that move settles.
    autoEnableSpeciesLayer (retry) {
      if (this.showSpeciesLayer || !this.map) return
      if ((this.map.getCamera().zoom ?? 0) >= OVERLAY_MIN_ZOOM) {
        this.setSpeciesLayerShown(true)
      } else if (retry) {
        this.map.events.addOnce('moveend', () => this.autoEnableSpeciesLayer(false))
      }
    },

    updateZoom () {
      if (this.map) this.currentZoom = this.map.getCamera().zoom
    },

    applyZoomLock () {
      const map = this.map
      if (!map) return
      if (this.showSpeciesLayer) {
        if (this.restrictedPrevMinZoom == null) this.restrictedPrevMinZoom = map.getCamera().minZoom ?? 1
        map.setCamera({ minZoom: OVERLAY_MIN_ZOOM })
      } else if (this.restrictedPrevMinZoom != null) {
        map.setCamera({ minZoom: this.restrictedPrevMinZoom })
        this.restrictedPrevMinZoom = null
      }
    },

    toggleTopo () {
      if (!this.map) return
      if (!this.topoLayer) {
        this.topoLayer = new atlas.layer.TileLayer({ ...MAP_OVERLAYS.topo })
        // stack bottom->top: topo, soil fill, species — so the vector fills stay visible above the raster
        this.map.layers.add(this.topoLayer, this.soilOpFillLayer || this.speciesFillLayer || this.layerCeiling)
      }
      this.topoVisible = !this.topoVisible
      this.topoLayer.setOptions({ visible: this.topoVisible })
    },

    // One-shot viewport scan (not a live layer); clicking again rescans the current viewport.
    analyzeSoilOperability () {
      const map = this.map
      if (!map) return
      if ((map.getCamera().zoom ?? 0) < HARVEST_MIN_ZOOM) {
        this.setSnack(this.$t('mustZoomInForOperability'))
        return
      }
      if (!this.soilOpSource) this.initSoilOperabilityLayers()
      this.setSoilOpLayersVisible(true)
      if (!this.soilOpEventsBound) {
        map.events.add('mousemove', this.soilOpFillLayer, this.showSoilTip)
        map.events.add('mousemove', this.maybeHideSoilTip)
        this.soilOpEventsBound = true
      }
      this.soilVisible = true
      this.scanCurrentViewport()
    },

    async scanCurrentViewport () {
      const map = this.map
      if (!map || !this.soilOpSource) return
      const bounds = map.getCamera().bounds
      if (!bounds) return
      const reqId = ++this.soilOpReqSeq
      if (this.soilOpAbort) this.soilOpAbort.abort()
      this.soilOpSource.clear() // a fresh scan replaces any previously analyzed area
      const controller = new AbortController()
      this.soilOpAbort = controller
      this.soilValidating = true
      try {
        const featureCollection = await fetchHarvestOperability(bounds, controller.signal)
        if (reqId !== this.soilOpReqSeq || !this.soilVisible || !this.soilOpSource) return
        this.soilOpSource.add(featureCollection.features)
      } catch (e) {
        if (e.name !== 'AbortError') {
          console.warn('Soil operability fetch failed', e)
          this.setSnackError(this.$t('operabilityUnavailable'))
        }
      } finally {
        if (reqId === this.soilOpReqSeq) this.soilValidating = false
      }
    },

    clearSoilOperability () {
      if (this.soilOpAbort) this.soilOpAbort.abort()
      this.soilValidating = false
      this.clearSoilTip()
      this.setSoilOpLayersVisible(false)
      if (this.soilOpSource) this.soilOpSource.clear()
      this.soilVisible = false
    },

    initSoilOperabilityLayers () {
      const map = this.map
      this.soilOpSource = new atlas.source.DataSource()
      map.sources.add(this.soilOpSource)
      const [fill, outline] = createPolygonLayersForDataSource(this.soilOpSource, PolygonStyle.SoilOperability)
      this.soilOpFillLayer = fill
      this.soilOpFillLayer.setOptions({ fillColor: SOIL_OP_FILL_COLOR, fillOpacity: this.soilOpAlpha })
      // Map-unit code labels: dark text + heavy white halo so they read over any fill color.
      const codeLabels = new atlas.layer.SymbolLayer(this.soilOpSource, null, {
        minZoom: HARVEST_MIN_ZOOM,
        iconOptions: { image: 'none' },
        textOptions: {
          textField: ['get', 'musym'],
          font: ['StandardFont-Bold'],
          size: 13,
          color: '#1b1b1b',
          haloColor: '#ffffff',
          haloWidth: 2,
          allowOverlap: false
        }
      })
      // bottom -> top: fill, outline, code labels
      this.soilOpLayers = [fill, outline, codeLabels]
      map.layers.add(this.soilOpLayers, this.speciesFillLayer || this.layerCeiling)
    },

    setSoilOpLayersVisible (isVisible) {
      if (!this.soilOpLayers) return
      this.soilOpLayers.forEach(layer => {
        const options = layer.getOptions()
        options.visible = isVisible
        layer.setOptions(options)
      })
    },

    showSoilTip (e) {
      const shape = e?.shapes?.[0]
      if (!shape) return
      const props = shape.getProperties ? shape.getProperties() : shape.properties
      if (!props) return
      this.soilOverFeature = true
      this.soilTip.html = this.getSoilTipContent(props)
      this.soilTip.x = e.pixel[0]
      this.soilTip.y = e.pixel[1]
      this.soilTip.show = true
    },

    maybeHideSoilTip () {
      // defer so the over-feature flag (set by the layer's mousemove) settles first
      Promise.resolve().then(() => {
        if (!this.soilOverFeature) this.soilTip.show = false
        this.soilOverFeature = false
      })
    },

    clearSoilTip () {
      this.soilTip.show = false
      this.soilOverFeature = false
    },

    // Cursor entered a legend box: drop the hover tooltips so they don't sit on top of the legend.
    hideDataTips () {
      this.clearSoilTip()
      this.clearSpeciesPopup()
      this.clearCustomTip()
    },

    toggleCustomLayersPanel () {
      this.customPanelOpen = !this.customPanelOpen
      if (this.customPanelOpen) this.loadCustomLayers()
    },

    async loadCustomLayers () {
      this.customLayersLoading = true
      try {
        const available = await listCustomLayers()
        const stored = readCustomLayerStyles()
        const taken = new Set(Object.values(stored).map(s => s.color))
        const previous = new Map(this.customLayers.map(l => [l.name, l]))
        const layers = available.map((layer, index) => {
          const prev = previous.get(layer.name)
          const style = stored[layer.name] ?? { color: this.pickCustomLayerColor(taken, index), opacity: CUSTOM_LAYER_DEFAULT_OPACITY }
          return {
            name: layer.name,
            label: layerLabel(layer.name),
            eTag: layer.eTag,
            lastModified: layer.lastModified,
            sizeBytes: layer.sizeBytes,
            visible: prev?.visible ?? stored[layer.name]?.visible ?? false,
            loading: !!this.customLayerHandles[layer.name]?.abort,
            color: style.color,
            opacity: style.opacity
          }
        })
        layers.sort((a, b) => ((stored[a.name]?.order ?? Infinity) - (stored[b.name]?.order ?? Infinity)) || a.label.localeCompare(b.label))
        const names = new Set(layers.map(l => l.name))
        previous.forEach((_, name) => { if (!names.has(name)) this.disposeCustomLayer(name) })
        this.customLayers = layers
        writeCustomLayerStyles(layers)
        this.applyOverlayOrder()
        layers
          .filter(l => l.visible && !this.customLayerHandles[l.name])
          .forEach(l => this.setCustomLayerVisible(l.name, true))
        layers
          .filter(l => l.visible && previous.has(l.name) && previous.get(l.name).eTag !== l.eTag)
          .forEach(l => this.loadCustomLayerFeatures(l))
      } catch (e) {
        console.warn('Custom layers list failed', e)
        this.setSnackError(this.$t('customLayersUnavailable'))
      } finally {
        this.customLayersLoading = false
      }
    },

    pickCustomLayerColor (taken, index) {
      const color = CUSTOM_LAYER_PALETTE.find(c => !taken.has(c)) ?? CUSTOM_LAYER_PALETTE[index % CUSTOM_LAYER_PALETTE.length]
      taken.add(color)
      return color
    },

    ensureCustomLayerHandle (layer) {
      const existing = this.customLayerHandles[layer.name]
      if (existing) return existing
      const source = new atlas.source.DataSource()
      this.map.sources.add(source)
      const layers = createPolygonLayersForDataSource(source, PolygonStyle.CustomLayer)
      const fill = layers.find(l => l.metadata.type === 'fill')
      const line = layers.find(l => l.metadata.type === 'line')
      fill.setOptions({ fillColor: layer.color, fillOpacity: layer.opacity })
      line.setOptions({ strokeColor: layer.color })
      this.map.layers.add(layers, this.layerCeiling)
      const handle = { source, layers, fill, line }
      this.customLayerHandles[layer.name] = handle
      return handle
    },

    disposeCustomLayer (name) {
      const handle = this.customLayerHandles[name]
      if (!handle || !this.map) return
      this.map.layers.remove(handle.layers)
      this.map.sources.remove(handle.source)
      delete this.customLayerHandles[name]
    },

    async setCustomLayerVisible (name, visible) {
      const layer = this.customLayers.find(l => l.name === name)
      if (!layer || !this.map) return
      layer.visible = visible
      writeCustomLayerStyles(this.customLayers)
      const created = !this.customLayerHandles[name]
      const handle = this.ensureCustomLayerHandle(layer)
      if (created) this.applyOverlayOrder()
      handle.layers.forEach(l => this.setSpeciesLayerVisibility(l, visible))
      if (!visible) {
        if (handle.abort) handle.abort.abort()
        this.clearCustomTip()
        return
      }
      this.bindOverlayMouseMove()
      await this.loadCustomLayerFeatures(layer)
    },

    async loadCustomLayerFeatures (layer) {
      const handle = this.customLayerHandles[layer.name]
      if (!handle) return
      if (handle.abort) handle.abort.abort()
      const controller = new AbortController()
      handle.abort = controller
      const live = () => this.customLayers.find(l => l.name === layer.name)
      layer.loading = true
      try {
        const features = await fetchCustomLayerFeatures(layer, controller.signal)
        if (!live()?.visible) return
        handle.source.clear()
        handle.source.add(features)
      } catch (e) {
        if (e?.name === 'AbortError') return
        console.warn(`Custom layer ${layer.name} failed`, e)
        const current = live()
        if (current) {
          current.visible = false
          writeCustomLayerStyles(this.customLayers)
        }
        handle.layers.forEach(l => this.setSpeciesLayerVisibility(l, false))
        this.clearCustomTip()
        this.setSnackError(this.$t('customLayerUnavailable', { layer: layer.label }))
      } finally {
        if (handle.abort === controller) {
          handle.abort = null
          const current = live()
          if (current) current.loading = false
        }
      }
    },

    reorderPanelLayers (ordered) {
      const speciesIndex = ordered.findIndex(row => row.kind === 'species')
      if (speciesIndex !== -1) {
        this.speciesOrder = speciesIndex
        localStorage.setItem(LocalStorageKeys.SPECIES_LAYER_ORDER, speciesIndex)
      }
      this.customLayers = ordered.filter(row => row.kind === 'custom').map(row => row.layer)
      this.applyOverlayOrder()
      writeCustomLayerStyles(this.customLayers)
    },

    applyOverlayOrder () {
      if (!this.map) return
      this.panelLayers.slice().reverse().forEach(row => {
        const layers = row.kind === 'species' ? this.speciesLayers : this.customLayerHandles[row.key]?.layers
        if (layers) layers.forEach(l => this.map.layers.move(l, this.layerCeiling))
      })
    },

    toggleCustomColorPicker (name) {
      this.customColorEditing = this.customColorEditing === name ? null : name
    },

    customPickerValue (layer) {
      return layer.color + Math.round(layer.opacity * 255).toString(16).padStart(2, '0').toUpperCase()
    },

    setCustomLayerStyle (name, hexa) {
      const layer = this.customLayers.find(l => l.name === name)
      if (!layer) return
      layer.color = hexa.substring(0, 7).toUpperCase()
      const alpha = parseInt(hexa.substring(7, 9), 16)
      if (!isNaN(alpha)) layer.opacity = Math.max(OVERLAY_MIN_OPACITY, alpha / 255)
      const handle = this.customLayerHandles[name]
      if (handle) {
        handle.fill.setOptions({ fillColor: layer.color, fillOpacity: layer.opacity })
        handle.line.setOptions({ strokeColor: layer.color })
      }
      writeCustomLayerStyles(this.customLayers)
    },

    bindOverlayMouseMove () {
      if (this.overlayEventsBound) return
      this.map.events.add('mousemove', this.onOverlayMouseMove)
      this.overlayEventsBound = true
    },

    // Layers are hit-tested in panel order so an overlapping lower layer never steals the tooltip.
    onOverlayMouseMove (e) {
      if (this.suppressTips || !e?.position) {
        this.clearOverlayTips()
        return
      }
      for (const row of this.panelLayers) {
        const fill = this.rowFillLayer(row)
        if (!fill) continue
        const shape = this.map.layers.getRenderedShapes(e.position, fill)[0]
        if (shape) {
          this.clearOverlayTips()
          if (row.kind === 'species') {
            this.showSpeciesTip(shape, e.pixel)
          } else {
            this.showCustomTip(shape, row.key, e.pixel)
          }
          return
        }
      }
      this.clearOverlayTips()
    },

    rowFillLayer (row) {
      if (row.kind === 'species') return this.showSpeciesLayer ? this.speciesFillLayer : null
      return row.layer.visible ? this.customLayerHandles[row.key]?.fill : null
    },

    clearOverlayTips () {
      this.clearSpeciesPopup()
      this.clearCustomTip()
    },

    showCustomTip (shape, name, pixel) {
      const props = shape.getProperties ? shape.getProperties() : shape.properties
      if (!props) return
      this.customTip.html = this.getCustomTipContent(name, props)
      this.customTip.x = pixel[0]
      this.customTip.y = pixel[1]
      this.customTip.show = true
    },

    clearCustomTip () {
      this.customTip.show = false
    },

    getCustomTipContent (name, props) {
      const css = 'color:#F9F6DF; background:#222; border-radius:6px; padding:8px 10px; font-size:13px; max-width:300px; pointer-events:none;'
      const label = this.customLayers.find(l => l.name === name)?.label ?? name
      const entries = Object.entries(props).filter(([key, value]) => !key.startsWith('_azureMaps') && value !== null && value !== undefined && value !== '')
      const rows = entries.slice(0, CUSTOM_TIP_MAX_ROWS)
        .map(([key, value]) => `<div class="ai-row"><b>${this.escHtml(key)}:</b> ${this.escHtml(typeof value === 'object' ? JSON.stringify(value) : value)}</div>`)
        .join('')
      const more = entries.length > CUSTOM_TIP_MAX_ROWS ? `<div class="ai-more">+${entries.length - CUSTOM_TIP_MAX_ROWS} ${this.$t('moreItems')}</div>` : ''
      return `<div style='${css}'>
        <div style="font-weight:bold; margin-bottom:4px">${this.escHtml(label)}</div>
        ${rows}${more}
      </div>`
    },

    toggleNearbyTracts (tract) {
      if (this.nearbyTractsVisible) {
        this.clearNearbyTracts()
      } else {
        this.showNearbyTracts(tract)
      }
    },

    ensureNearbyTracts () {
      if (this.nearbyTracts) return Promise.resolve(this.nearbyTracts)
      if (!this.nearbyTractsPromise) {
        this.nearbyTractsPromise = this.fetchTractsOnce(new TractFilter({ includeInitiated: true, includeCruised: true, includeExecuted: true, includeClosed: true }))
          .then(tracts => { this.nearbyTracts = tracts; return tracts })
          .finally(() => { this.nearbyTractsPromise = null })
      }
      return this.nearbyTractsPromise
    },

    async showNearbyTracts ({ tractId }) {
      if (!this.map) return
      const reqId = ++this.nearbyTractsReqSeq
      this.nearbyTractsVisible = true
      this.nearbyTractsLoading = true
      if (!this.allTractHudSections) {
        try {
          await this.fetchTractHudSections()
        } catch (e) {
          console.error(e)
        }
      }
      try {
        const tracts = await this.ensureNearbyTracts()
        if (reqId !== this.nearbyTractsReqSeq) return
        this.nearbyGatedTracts = tracts
          .filter(t => t.tractId !== tractId)
          .sort((a, b) => b.spot.latitude - a.spot.latitude)
        this.syncNearbyTractMarkers()
      } catch (e) {
        if (reqId !== this.nearbyTractsReqSeq) return
        this.clearNearbyTracts()
        if (e.message === 'REQ_IN_FLIGHT') return
        console.warn('Nearby tracts fetch failed', e)
        this.setSnackError(this.$t('nearbyTractsUnavailable'))
      } finally {
        if (reqId === this.nearbyTractsReqSeq) this.nearbyTractsLoading = false
      }
    },

    clearNearbyTracts () {
      this.nearbyTractsReqSeq++
      this.nearbyTractsLoading = false
      this.nearbyTractMarkers.forEach(marker => {
        marker.options.popup.remove()
        if (this.map) this.map.markers.remove(marker)
      })
      this.nearbyTractMarkers.clear()
      this.nearbyGatedTracts = []
      this.nearbyTractsHidden = false
      this.openNearbyMarker = { marker: null, justAdded: false }
      this.nearbyTractsVisible = false
      this.hideNearbyTractBoundaries()
    },

    syncNearbyTractMarkers () {
      if (!this.map || !this.nearbyTractsVisible) return
      const hidden = viewportWidthMiles(this.map) > MAX_VIEWPORT_MILES_FOR_TRACT_MARKERS
      const bounds = this.map.getCamera().bounds
      const inView = hidden ? [] : this.nearbyGatedTracts.filter(t => atlas.data.BoundingBox.containsPosition(bounds, [t.spot.longitude, t.spot.latitude]))
      const inViewIds = new Set(inView.map(t => t.tractId))
      this.nearbyTractMarkers.forEach((marker, tractId) => {
        if (inViewIds.has(tractId)) return
        if (this.openNearbyMarker.marker === marker) {
          marker.options.popup.remove()
          this.openNearbyMarker.marker = null
        }
        this.map.markers.remove(marker)
        this.nearbyTractMarkers.delete(tractId)
      })
      inView.filter(t => !this.nearbyTractMarkers.has(t.tractId)).forEach(tract => {
        const progress = harvestedProgress(tract)
        const marker = new atlas.HtmlMarker({
          htmlContent: tractMarkerSvg(tract, progress),
          position: [tract.spot.longitude, tract.spot.latitude],
          popup: tractPopup(tract, progress)
        })
        this.map.events.add('click', marker, () => this.onNearbyMarkerClicked(marker))
        this.map.markers.add(marker)
        this.nearbyTractMarkers.set(tract.tractId, marker)
      })
      this.nearbyTractsHidden = hidden && this.nearbyGatedTracts.length > 0
    },

    onNearbyMarkerClicked (marker) {
      if (this.openNearbyMarker.marker?.options.popup) this.openNearbyMarker.marker.options.popup.remove()
      this.openNearbyMarker = { marker, justAdded: true }
      marker.togglePopup()
    },

    closeNearbyPopup () {
      const { marker, justAdded } = this.openNearbyMarker
      if (marker && !justAdded) {
        marker.options.popup.remove()
        this.openNearbyMarker.marker = null
      }
      this.openNearbyMarker.justAdded = false
    },

    toggleNearbyTractBoundaries (tract) {
      if (this.nearbyBoundariesVisible) {
        this.hideNearbyTractBoundaries()
      } else {
        this.showNearbyTractBoundaries(tract)
      }
    },

    async showNearbyTractBoundaries ({ tractId, spot }) {
      if (!this.map) return
      const center = { latitude: Number(spot?.latitude), longitude: Number(spot?.longitude) }
      if (!this.nearbyBoundarySources) this.initNearbyBoundaryLayers()
      const reqId = ++this.nearbyBoundariesReqSeq
      this.nearbyBoundariesVisible = true
      try {
        const tracts = await this.ensureNearbyTracts()
        const nearby = tracts.filter(t => t.tractId !== tractId && milesBetween(center, t.spot) <= NEARBY_TRACT_RADIUS_MILES)
        const uncached = nearby.filter(t => !this.nearbyBoundaryCache.has(t.tractId))
        if (uncached.length > 0) {
          this.nearbyBoundariesProgress = { done: 0, total: uncached.length }
          this.nearbyBoundariesLoading = true
          const geojsons = await Promise.all(uncached.map(async t => {
            const geojson = await angelGeojsonClient.readGeojson(t.tractId)
            if (reqId === this.nearbyBoundariesReqSeq) this.nearbyBoundariesProgress.done++
            return geojson
          }))
          uncached.forEach((t, i) => this.nearbyBoundaryCache.set(t.tractId, new AngelGeojson(geojsons[i]).getFeatures(CUT) ?? []))
        }
        if (reqId !== this.nearbyBoundariesReqSeq) return
        const withCuts = nearby.filter(t => this.nearbyBoundaryCache.get(t.tractId).length > 0)
        if (withCuts.length === 0) {
          this.hideNearbyTractBoundaries()
          this.setSnack(this.$t('noNearbyBoundaries', { miles: NEARBY_TRACT_RADIUS_MILES }))
          return
        }
        const groups = NEARBY_TRACT_STATUSES.map(status => ({ status, tracts: withCuts.filter(t => t.status === status.value) }))
        groups.forEach(({ status, tracts }) => {
          const source = this.nearbyBoundarySources.get(status.value)
          source.clear()
          source.add(tracts.flatMap(t => this.nearbyBoundaryCache.get(t.tractId)))
        })
        this.nearbyBoundaryRows = groups
          .filter(({ tracts }) => tracts.length > 0)
          .map(({ status, tracts }) => ({ status, count: tracts.length, swatch: styleFromPolygonType(nearbyBoundaryStyle(status)) }))
        this.setNearbyBoundaryLayersVisible(true)
      } catch (e) {
        if (reqId !== this.nearbyBoundariesReqSeq) return
        this.hideNearbyTractBoundaries()
        if (e.message === 'REQ_IN_FLIGHT') return
        console.warn('Nearby tract boundaries fetch failed', e)
        this.setSnackError(this.$t('nearbyBoundariesUnavailable'))
      } finally {
        if (reqId === this.nearbyBoundariesReqSeq) this.nearbyBoundariesLoading = false
      }
    },

    hideNearbyTractBoundaries () {
      this.nearbyBoundariesReqSeq++
      this.nearbyBoundariesLoading = false
      this.setNearbyBoundaryLayersVisible(false)
      this.nearbyBoundariesVisible = false
    },

    initNearbyBoundaryLayers () {
      this.nearbyBoundarySources = new Map(NEARBY_TRACT_STATUSES.map(status => [status.value, new atlas.source.DataSource()]))
      this.map.sources.add([...this.nearbyBoundarySources.values()])
      this.nearbyBoundariesLayers = NEARBY_TRACT_STATUSES.flatMap(status => createPolygonLayersForDataSource(this.nearbyBoundarySources.get(status.value), nearbyBoundaryStyle(status)))
      this.map.layers.add(this.nearbyBoundariesLayers, this.layerCeiling)
    },

    setNearbyBoundaryLayersVisible (isVisible) {
      if (!this.nearbyBoundariesLayers) return
      this.nearbyBoundariesLayers.forEach(layer => {
        const options = layer.getOptions()
        options.visible = isVisible
        layer.setOptions(options)
      })
    },

    toggleNearbyParcels () {
      if (!this.map) return
      if (this.nearbyParcelsVisible) {
        this.hideNearbyParcels()
      } else if ((this.map.getCamera().zoom ?? 0) < MIN_ZOOM_FOR_PARCEL_TILES) {
        this.setSnack(this.$t('mustZoomInToViewParcels'))
      } else {
        if (!this.parcelTilesDataSource) this.initParcelTiles()
        this.map.layers.add(this.parcelTileLayers)
        this.map.layers.add(this.focusedParcelLayers)
        this.nearbyParcelsVisible = true
      }
    },

    hideNearbyParcels () {
      this.map.layers.remove(this.parcelTileLayers)
      this.map.layers.remove(this.focusedParcelLayers)
      this.focusedParcelDataSource.clear()
      this.closeParcelPopup()
      this.nearbyParcelsVisible = false
    },

    initParcelTiles () {
      this.parcelTilesDataSource = new atlas.source.VectorTileSource(null, {
        tiles: [getParcelVectorTilesUrl()],
        maxZoom: 17,
        minZoom: MIN_ZOOM_FOR_PARCEL_TILES
      })
      this.map.sources.add(this.parcelTilesDataSource)
      this.parcelTileLayers = createPolygonLayersForDataSource(this.parcelTilesDataSource, PolygonStyle.NearbyParcels, 'parcels')
      this.parcelTileFillLayer = this.parcelTileLayers.find(l => l.metadata.type === 'fill')
      this.parcelTileFillLayer.setOptions({ fillOpacity: 0 })

      this.focusedParcelDataSource = new atlas.source.DataSource()
      this.map.sources.add(this.focusedParcelDataSource)
      this.focusedParcelLayers = createPolygonLayersForDataSource(this.focusedParcelDataSource, PolygonStyle.FocusedParcel)

      this.map.events.add('click', this.closeParcelPopup)
      this.map.events.add('click', this.parcelTileLayers, this.onParcelTileLayerClicked)
      this.map.events.add('mousemove', this.parcelTileFillLayer, this.setParcelTileFillConditionallyHighlighted)
      this.map.events.add('mouseleave', this.parcelTileFillLayer, this.setParcelTileFillTransparent)
    },

    setParcelTileFillTransparent () {
      this.parcelTileFillLayer.setOptions({ fillOpacity: 0 })
    },

    setParcelTileFillConditionallyHighlighted (e) {
      this.parcelTileFillLayer.setOptions({
        fillOpacity: [
          'case',
          this.expressionRobustIdMatchesEventShape(e),
          PolygonStyle.NearbyParcels.fillOpacity,
          0
        ]
      })
    },

    expressionRobustIdMatchesEventShape (e) {
      return ['==', ['get', 'robust_id'], this.getRobustIdFromEvent(e)]
    },

    getRobustIdFromEvent (e) {
      return e?.shapes[0]?._vectorTileFeature?.properties?.robust_id
    },

    async onParcelTileLayerClicked (e) {
      // marker clicks bubble through to the parcel layer underneath
      if (e.originalEvent?.target !== this.map.getCanvas()) return
      this.closeParcelPopup()
      if (this.getRobustIdFromEvent(e)) await this.openPopupWithParcelDetails(e)
    },

    closeParcelPopup () {
      if (!this.parcelPopup) return
      this.parcelPopup.remove()
      this.parcelPopup = null
    },

    async openPopupWithParcelDetails (e) {
      const reqId = ++this.parcelPopupReqSeq
      const parcelResponse = await getParcelDetailsByRobustId(this.getRobustIdFromEvent(e))
      if (reqId !== this.parcelPopupReqSeq) return
      this.focusedParcelDataSource.setShapes(this.getFeatureFromParcelResponse(parcelResponse))
      const popup = this.getParcelPopup(this.getFieldsFromParcelResponse(parcelResponse), this.getParcelPopupLonLatFromEvent(e))
      popup.open(this.map)
      this.parcelPopup = popup
    },

    getFeatureFromParcelResponse (parcelResponse) {
      return parcelResponse.parcels.features[0]
    },

    getFieldsFromParcelResponse (parcelResponse) {
      return this.getFeatureFromParcelResponse(parcelResponse).properties.fields
    },

    getParcelPopupLonLatFromEvent (e) {
      const clickCoords = e.position
      if (this.map.getCamera().zoom > 14.5) {
        return clickCoords
      } else {
        const { latitude, longitude } = e.shapes[0].properties
        const parcelCoords = [longitude, latitude]
        if (turf.distance(turf.point(parcelCoords), turf.point(clickCoords), { units: 'miles' }) > 0.25) {
          return clickCoords
        }
        return parcelCoords
      }
    },

    getParcelPopup (parcel, position) {
      const css =
        `color: #F9F6DF;
        font-weight: bold;
        font-size: 20px;
        padding: 7px;`
      const interiorHtml =
        `
        <span class="map-marker-popup-line">${this.$t('parcelNumber')}:</span>
        <br>
        <span class="map-marker-popup-line text-right fill-width d-block">${parcel?.parcel_id ?? this.$t('unknown')}</span>

        <span class="map-marker-popup-line">${this.$t('owner')}:</span>
        <br>
        <span class="map-marker-popup-line text-right fill-width d-block">${parcel?.owner ?? this.$t('unknown')}</span>

        <span class="map-marker-popup-line">${this.$t('deededAcres')}:</span>
        <br>
        <span class="map-marker-popup-line text-right fill-width d-block">${parcel?.acreage ?? this.$t('unknown')}</span>
        <div class="ticket-popup-tail"></div>
        `
      const popupOptions = {
        content: `<div style='${css}'>${interiorHtml}</div>`,
        pixelOffset: [0, -8],
        closeButton: false,
        showPointer: true,
        position
      }

      return new atlas.Popup(popupOptions)
    },

    getSoilTipContent (props) {
      const css = 'color:#F9F6DF; background:#222; border-radius:6px; padding:8px 10px; font-size:13px; max-width:260px; pointer-events:none;'
      const subtitle = props.muname ? `${props.musym} · ${props.muname}` : (props.musym ?? '')
      return `<div style='${css}'>
        <div style="font-weight:bold; margin-bottom:2px">${this.$t('operability')}</div>
        <div style="opacity:0.85; margin-bottom:4px">${subtitle}</div>
        <div><b>${props.rating ?? ''}</b></div>
      </div>`
    },

    toggleSpeciesLayer () {
      if (!this.canShowSpeciesLayer) return
      const map = this.map
      if (!map) return
      if (!this.showSpeciesLayer && (map.getCamera().zoom ?? 0) < OVERLAY_MIN_ZOOM) {
        this.setSnack(this.$t('mustZoomInForSpeciesLayer'))
        return
      }
      this.setSpeciesLayerShown(!this.showSpeciesLayer)
    },

    setSpeciesLayerShown (shown) {
      this.currentZoom = this.map.getCamera().zoom
      this.showSpeciesLayer = shown
      if (shown) {
        this.enableSpeciesLayer()
      } else {
        this.disableSpeciesLayer()
      }
      this.applyZoomLock()
    },

    async enableSpeciesLayer () {
      const map = this.map
      if (!map) return
      if (this.allMitigationGroups.length === 0) {
        this.speciesLayerEnabling = true
        try {
          await this.fetchMitigationGroups()
        } catch (e) {
          console.error(e) // no groups → shardAdapter falls back to template colors
        } finally {
          this.speciesLayerEnabling = false
        }
      }
      if (!this.speciesSource) this.initSpeciesLayer()
      this.applyOverlayOrder()
      this.setSpeciesLayersVisible(true)
      if (!this.speciesEventsBound) {
        map.events.add('moveend', this.refreshSpeciesLayer)
        this.speciesEventsBound = true
      }
      this.bindOverlayMouseMove()
      this.refreshSpeciesLayer()
    },

    repaintSpeciesLayer () {
      if (this.speciesLayerEnabling || !this.showSpeciesLayer || !this.speciesSource) return
      this.speciesSource.clear()
      this.speciesTileLru.clear()
      this.refreshSpeciesLayer()
    },

    disableSpeciesLayer () {
      this.clearSpeciesPopup()
      this.setSpeciesLayersVisible(false)
      if (this.speciesSource) this.speciesSource.clear()
      if (this.speciesTileLru) this.speciesTileLru.clear()
    },

    initSpeciesLayer () {
      const map = this.map
      this.speciesSource = new atlas.source.DataSource()
      map.sources.add(this.speciesSource)
      this.speciesLayers = createPolygonLayersForDataSource(this.speciesSource, PolygonStyle.SpeciesLayer)
      this.speciesFillLayer = this.speciesLayers.find(l => l.metadata.type === 'fill')
      this.speciesFillLayer.setOptions({ fillColor: ['get', '_fill'] })
      const speciesLineLayer = this.speciesLayers.find(l => l.metadata.type === 'line')
      if (speciesLineLayer) speciesLineLayer.setOptions({ strokeColor: ['get', '_fill'] })
      map.layers.add(this.speciesLayers, this.layerCeiling)
      this.speciesTileLru = new LruCache(SPECIES_TILE_CAPACITY, (key, ids) => {
        if (this.speciesSource && ids && ids.length) this.speciesSource.remove(ids)
      })
    },

    setSpeciesLayersVisible (isVisible) {
      if (!this.speciesLayers) return
      this.speciesLayers.forEach(layer => this.setSpeciesLayerVisibility(layer, isVisible))
    },

    setSpeciesLayerVisibility (layer, isVisible) {
      const options = layer.getOptions()
      options.visible = isVisible
      layer.setOptions(options)
    },

    async refreshSpeciesLayer () {
      const map = this.map
      if (!this.showSpeciesLayer || !map || !this.speciesSource) return
      const bounds = map.getCamera().bounds
      if (!bounds) return
      const tiles = tilesForBounds(bounds)
      if (tiles.length === 0 || tiles.length > MAX_TILES) return
      // .get() marks in-view tiles MRU so the LRU only evicts off-screen ones
      const fresh = []
      tiles.forEach(t => {
        if (this.speciesTileLru.get(`${t.z}/${t.x}/${t.y}`) === undefined) fresh.push(t)
      })
      if (fresh.length === 0) return
      const results = await Promise.all(fresh.map(async t => ({ t, fc: await fetchTile(t.z, t.x, t.y) })))
      if (!this.showSpeciesLayer || !this.speciesSource) return // toggled off mid-fetch
      const features = []
      results.forEach(({ t, fc }) => {
        const key = `${t.z}/${t.x}/${t.y}`
        if (this.speciesTileLru.has(key)) return // added by a concurrent refresh
        const ids = []
        const feats = fc.features || []
        feats.forEach(f => {
          const rf = adaptFeature(f, this.speciesLayerConfig)
          if (!rf) return
          if (rf.id != null) ids.push(rf.id)
          features.push(rf)
        })
        this.speciesTileLru.set(key, ids)
      })
      if (features.length) this.speciesSource.add(features)
    },

    showSpeciesTip (shape, pixel) {
      const props = shape.getProperties ? shape.getProperties() : shape.properties
      if (!props) return
      const hexId = shape.getId ? shape.getId() : shape.id
      this.speciesTip.html = this.getSpeciesPopupContent(props, hexId)
      this.speciesTip.x = pixel[0]
      this.speciesTip.y = pixel[1]
      this.speciesTip.show = true
    },

    clearSpeciesPopup () {
      this.speciesTip.show = false
    },

    getSpeciesPopupContent (props, hexId) {
      const css = 'color:#F9F6DF; background:#222; border-radius:6px; padding:10px; font-size:13px; max-width:300px; pointer-events:none;'
      const interiorHtml = `
        <div class="ai-title"><span>${this.speciesPopupTitle(props)}</span>${this.renderBadge(props.angelInfo?.group)}</div>
        ${this.renderAngelInfo(props.angelInfo, hexId)}
      `
      return `<div style='${css}'>${interiorHtml}</div>`
    },

    // requiresHabitat → endangered species; any other matched group → ecological priority; unmatched → generic.
    speciesPopupTitle (props) {
      const match = this.speciesLayerConfig?.owningGroup?.(props.groups ?? [])
      if (!match) return this.$t('endangeredSpecies')
      return match.requiresHabitat ? this.$t('endangeredSpecies') : this.$t('ecologicalPriority')
    },

    renderAngelInfo (info, hexId) {
      const parts = []
      if (hexId != null) parts.push(`<div class="ai-heading">${this.escHtml(hexId)}</div>`)
      for (const key of ANGEL_SECTION_ORDER) {
        const section = info?.[key]
        const handler = section && ANGEL_RENDERERS[section.render]
        if (handler) parts.push(this[handler](section))
      }
      return parts.join('')
    },

    escHtml (s) {
      return String(s ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]))
    },

    groupLabel (entry) {
      const name = this.speciesLayerConfig?.nameFor?.(entry)
      if (name) return name
      if (entry?.key) return `Group ${String(entry.key).replace(/^group/i, '')}`
      return entry?.id != null ? `Group ${entry.id}` : ''
    },

    badgeEntriesByPrecedence (entries) {
      const ranked = this.speciesLayerConfig?.groupsByPrecedence?.(entries) ?? []
      const rankById = new Map(ranked.map((group, rank) => [group.mitigationGroupId, rank]))
      const rankOf = entry => rankById.get(entry?.id) ?? Number.MAX_SAFE_INTEGER
      return entries.slice().sort((a, b) => rankOf(a) - rankOf(b))
    },

    renderBadge (section) {
      const entries = normalizeGroups(section?.value)
      if (!entries.length) return ''
      const label = this.badgeEntriesByPrecedence(entries).map(e => this.groupLabel(e)).filter(l => l).join(' · ')
      return `<span class="ai-badge" style="background:${resolveFill(entries, this.speciesLayerConfig)}">${this.escHtml(label)}</span>`
    },

    renderList (section) {
      const items = section.value || []
      const label = section.label ? this.$t(section.label) : ''
      if (!items.length) return `<div class="ai-empty">${this.$t('noneRecorded')}</div>`
      const rows = items.slice(0, SPECIES_LIST_MAX).map(([p, s]) => `<div class="ai-row">${this.escHtml(p)}${s ? ` <em>${this.escHtml(s)}</em>` : ''}</div>`).join('')
      const more = items.length > SPECIES_LIST_MAX ? `<div class="ai-more">+${items.length - SPECIES_LIST_MAX} ${this.$t('moreItems')}</div>` : ''
      return `<div class="ai-label">${this.escHtml(label)}</div>${rows}${more}`
    }
  }
}
</script>

<style scoped>
/* no box of its own; the absolute tooltip + warning anchor to the map container */
.map-overlays {
  display: contents;
}

/* popup body injected via v-html; ::v-deep lets scoped styles reach it */
.species-hex-tip ::v-deep .ai-heading {
  font-weight: bold;
  margin: 6px 0 2px;
}
.species-hex-tip ::v-deep .ai-title {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 8px;
  margin-bottom: 6px;
  font-weight: bold;
  font-size: 14px;
}
.species-hex-tip ::v-deep .ai-badge {
  margin-left: auto;
  border-radius: 3px;
  padding: 1px 6px;
  color: #fff;
  font-size: 12px;
  font-weight: normal;
}
.species-hex-tip ::v-deep .ai-row {
  padding: 1px 0;
}
.species-hex-tip ::v-deep .ai-label {
  font-weight: bold;
  opacity: 0.85;
}
.species-hex-tip ::v-deep .ai-row em {
  opacity: 0.7;
  font-style: italic;
}
.species-hex-tip ::v-deep .ai-empty {
  opacity: 0.8;
}
.species-hex-tip ::v-deep .ai-more {
  opacity: 0.8;
  margin-top: 4px;
}

.map-legends {
  position: absolute;
  top: 64px;
  right: 10px;
  z-index: 21; /* above the toolbar row (z-index 20) so the legends' clear buttons stay clickable */
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  pointer-events: none;
}
.map-legend {
  background: rgba(34, 34, 34, 0.867);
  color: #fff;
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 12px;
  pointer-events: auto;
}
.map-legend-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 6px;
}
.map-legend-title {
  font-weight: 700;
}
.map-legend-clear {
  opacity: 0.7;
}
.map-legend-clear:hover {
  opacity: 1;
}
.map-legend-row {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 1px 0;
}
.map-legend-swatch {
  width: 12px;
  height: 12px;
  border-radius: 2px;
  display: inline-block;
  flex: 0 0 auto;
}
.map-legend-alpha {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  pointer-events: auto;
}
.map-legend-alpha-label {
  white-space: nowrap;
}
.map-legend-alpha-value {
  min-width: 32px;
  text-align: right;
  opacity: 0.85;
  font-variant-numeric: tabular-nums;
}
.map-legend-slider {
  flex: 1 1 auto;
  min-width: 90px;
  accent-color: #D15F27;
  cursor: pointer;
}

.custom-layers-panel {
  min-width: 252px;
  max-height: 60vh;
  overflow-y: auto;
}
.custom-layer + .custom-layer {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.15);
}
.custom-layer-grip {
  cursor: grab;
  opacity: 0.7;
}
.custom-layer-toggle {
  cursor: pointer;
}
.custom-layer-toggle input {
  accent-color: #D15F27;
  margin: 0;
}
.custom-layer-name {
  font-weight: 600;
}
.custom-layer-color {
  width: 16px;
  height: 16px;
  margin-left: auto;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 50%;
  cursor: pointer;
}
.custom-layer-picker {
  margin-top: 6px;
}
.custom-layer-note {
  opacity: 0.75;
  margin-top: 4px;
}

.harvest-validating {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 4;
  display: flex;
  align-items: center;
  gap: 10px;
  pointer-events: none;
  background: rgba(34, 34, 34, 0.92);
  color: #fff;
  padding: 12px 18px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
}
</style>
